import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { userOtpSchema } from "validation";
import OtpInput from "react-otp-input";
import { useApiMutation } from "hooks/useApi";
import { Button } from "components/global";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { auth } from "../../../lib/firebase";
import { RecaptchaVerifier, signInWithPhoneNumber, PhoneAuthProvider, signInWithCredential } from "firebase/auth";

export default function OTPForm({ phoneNumber, onSuccess }) {
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(0);
  const [isFirebaseEnabled, setIsFirebaseEnabled] = useState(true);
  const [verificationId, setVerificationId] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [shouldSendOTP, setShouldSendOTP] = useState(false);
  const [recaptchaV3Ready, setRecaptchaV3Ready] = useState(false);

  const recaptchaVerifier = useRef(null);
  const timerRef = useRef(null);

  // reCAPTCHA v3 configuration
  // const recaptchaV2SiteKey = '6LdIjnYrAAAAALegjvPmX2pCeiH_8jRAuQqiUT96'; // Replace with your v2 site key
  const recaptchaV3SiteKey = '6Ld-nnYrAAAAAI0v3VhA4dX30pmCLA34TVLTvYhZ'; // Replace with your v3 site key

  const { executeMutation: generateCustomOTP, isMutating: customOtpSending } = useApiMutation("/auth/generate-custom-otp");
  const { executeMutation: verifyCustomOtp, isMutating: customOtpVerifying } = useApiMutation("/auth/verify-custom-otp");
  const { executeMutation: verifyOtpFirebase, isMutating: firebaseOtpVerifying } = useApiMutation("/firebase/verify-otp-token");
  const { register, handleSubmit, formState: { errors }, setValue, reset } = useForm({
    resolver: zodResolver(userOtpSchema)
  });

  // Format phone number consistently
  const formatPhone = (phone) => phone.startsWith('+') ? phone.replace(/\s/g, '') : `+${phone.replace(/\s/g, '')}`;

  function IsFirebaseEnabled(phoneNumber) {
    const customPatterns = ["^\\+2011"];
    return !customPatterns.some(pattern => new RegExp(pattern).test(formatPhone(phoneNumber)));
  }

  // Load reCAPTCHA v3 script
  useEffect(() => {
    const loadRecaptchaScript = () => {
      return new Promise((resolve, reject) => {
        // Check if script already exists
        if (window.grecaptcha) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaV3SiteKey}`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    };

    loadRecaptchaScript()
      .then(() => {
        // Wait for grecaptcha to be ready
        if (window.grecaptcha) {
          window.grecaptcha.ready(() => {
            setRecaptchaV3Ready(true);
          });
        }
      })
      .catch((error) => {
        console.error('Failed to load reCAPTCHA script:', error);
        toast.error("خطأ في تحميل نظام التحقق");
      });
  }, [recaptchaV3SiteKey]);

  // Initialize based on phone number
  useEffect(() => {
    const formattedPhone = formatPhone(phoneNumber);
    const firebaseEnabled = IsFirebaseEnabled(formattedPhone);
    setIsFirebaseEnabled(firebaseEnabled);

    if (!firebaseEnabled) {
      // For custom OTP, wait for reCAPTCHA v3 to be ready
      if (recaptchaV3Ready) {
        setLoading(false);
        setShouldSendOTP(true);
      }
      return;
    }

    // Only initialize Firebase reCAPTCHA if Firebase is enabled
    let container = null;
    const initFirebaseRecaptcha = async () => {
      try {
        container = document.createElement("div");
        container.id = `recaptcha-${Date.now()}`;
        container.style.cssText = "position:absolute;left:-9999px;top:-9999px;";
        document.body.appendChild(container);

        recaptchaVerifier.current = new RecaptchaVerifier(auth, container.id, {
          size: 'invisible',
          callback: () => console.log('Firebase reCAPTCHA solved'),
          'expired-callback': () => toast.error("انتهت صلاحية التحقق، يرجى المحاولة مرة أخرى")
        });

        await recaptchaVerifier.current.render();
        setLoading(false);
        setShouldSendOTP(true);
      } catch (error) {
        console.error('Firebase reCAPTCHA error:', error);
        toast.error("خطأ في التحقق من reCAPTCHA");
        setLoading(false);
      }
    };

    initFirebaseRecaptcha();

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (recaptchaVerifier.current) {
        try {
          recaptchaVerifier.current.clear();
        } catch (e) {
          console.warn('Cleanup warning:', e);
        }
      }
      if (container?.parentNode) container.parentNode.removeChild(container);
    };
  }, [phoneNumber, recaptchaV3Ready]);

  // Handle OTP sending after initialization
  useEffect(() => {
    const sendOTPAsync = async () => {
      if (shouldSendOTP && !sent) {
        try {
          await sendOTP();
        } catch (error) {
          console.error('Failed to send OTP:', error);
          toast.error("فشل في إرسال رمز التحقق");
        } finally {
          setShouldSendOTP(false);
        }
      }
    };

    sendOTPAsync();
  }, [shouldSendOTP, sent]);

  // Timer effect
  useEffect(() => {
    if (timer > 0) {
      timerRef.current = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timer]);

  // Sync OTP with form
  useEffect(() => {
    setValue("otp", otp);
  }, [otp, setValue]);

  // Execute reCAPTCHA v3 for custom OTP
  const executeRecaptchaV3 = async (action) => {
    if (!window.grecaptcha || !recaptchaV3Ready) {
      throw new Error("reCAPTCHA not ready");
    }

    return new Promise((resolve, reject) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(recaptchaV3SiteKey, { action })
          .then(resolve)
          .catch(reject);
      });
    });
  };

  const sendFirebaseOTP = async (formattedPhone) => {
    const confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, recaptchaVerifier.current);
    setVerificationId(confirmationResult.verificationId);
  };

  const sendCustomOTP = async (formattedPhone) => {
    try {
      // Execute reCAPTCHA v3 with 'generate_otp' action
      const recaptchaToken = await executeRecaptchaV3('generate_otp');

      const response = await generateCustomOTP("POST", {
        phone: formattedPhone,
        recaptchaToken: recaptchaToken
      });

      return response;
    } catch (error) {
      console.error('reCAPTCHA v3 error:', error);
      throw new Error("فشل في التحقق من reCAPTCHA");
    }
  };

  const sendOTP = async () => {
    if (isFirebaseEnabled && !recaptchaVerifier.current) return;
    if (!isFirebaseEnabled && !recaptchaV3Ready) return;

    try {
      setSending(true);
      const formattedPhone = formatPhone(phoneNumber);

      if (isFirebaseEnabled) {
        await sendFirebaseOTP(formattedPhone);
      } else {
        await sendCustomOTP(formattedPhone);
      }

      setSent(true);
      toast.success("تم إرسال رمز التحقق!");
      setTimer(60);
    } catch (error) {
      console.error('Send OTP error:', error);
      toast.error("فشل في إرسال رمز التحقق: " + error.message);
    } finally {
      setSending(false);
    }
  };

  const onSubmit = async (data) => {
    if (!data.otp) {
      toast.error("يرجى إدخال رمز التحقق");
      return;
    }

    // Check if OTP was sent successfully
    if (isFirebaseEnabled && !verificationId) {
      toast.error("لم يتم إرسال رمز التحقق بعد");
      return;
    }

    if (!isFirebaseEnabled && !sent) {
      toast.error("لم يتم إرسال رمز التحقق بعد");
      return;
    }

    try {
      let response;
      if (isFirebaseEnabled) {
        const credential = PhoneAuthProvider.credential(verificationId, data.otp);
        const result = await signInWithCredential(auth, credential);
        const idToken = await result.user.getIdToken();

        response = await verifyOtpFirebase("POST", {
          idToken,
          phone: formatPhone(phoneNumber)
        });
      } else {
        // Optional: Add reCAPTCHA v3 verification for OTP verification as well
        let recaptchaToken = null;
        try {
          recaptchaToken = await executeRecaptchaV3('verify_otp');
        } catch (error) {
          console.warn('reCAPTCHA v3 verification failed, proceeding without it:', error);
        }

        response = await verifyCustomOtp("POST", {
          phone: formatPhone(phoneNumber),
          otp: data.otp,
          ...(recaptchaToken && { recaptchaToken })
        });
        console.log("Custom OTP verification response:", response); // Add this line
      }

      toast.success("تم تأكيد الرقم بنجاح!");
      onSuccess(response);
    } catch (error) {
      console.error('Verify OTP error:', error);
      toast.error("فشل في التحقق من الرمز: " + error.message);
      setOtp("");
      reset();
    }
  };

  const handleResend = async () => {
    if (timer > 0) return;

    setVerificationId("");
    setOtp("");
    setSent(false);
    setTimer(0);
    reset();

    setShouldSendOTP(true);
  };

  // Get current loading state
  const isLoading = loading || (isFirebaseEnabled ? firebaseOtpVerifying : customOtpVerifying);
  const isSending = sending || customOtpSending;

  if (loading) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="text-gray-600">
          {isFirebaseEnabled ? "جاري التحضير..." : "جاري تحميل نظام التحقق..."}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 mt-6 mx-auto container md:max-w-2xl">
      {isSending && (
        <div className="bg-blue-50 p-4 rounded-lg text-center">
          <p className="text-blue-600">جاري إرسال الرمز...</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="text-center">
          <p className="text-lg text-gray-700 mb-2">
            {sent ? "أدخل الرمز المرسل إلى" : "سيتم إرسال رمز التحقق إلى"}
          </p>
          <p className="font-bold text-xl bg-gray-100 rounded-lg py-2 px-4 inline-block">{phoneNumber}</p>
        </div>

        {/* Auto-sending message for custom OTP */}
        {!isFirebaseEnabled && !sent && !isSending && (
          <div className="bg-green-50 p-4 rounded-lg text-center">
            <p className="text-green-600">جاري إرسال رمز التحقق تلقائياً...</p>
          </div>
        )}

        {/* OTP, Resend, and Submit sections */}
        <div className="h-48">
          {sent && (
            <>
              <div className="flex justify-center" dir="ltr">
                <OtpInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={6}
                  inputType="tel"
                  shouldAutoFocus={true}
                  renderInput={(props, index) => (
                    <input
                      {...props}
                      className={`text-center text-2xl font-bold border-2 !w-12 !h-14 mx-1 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${otp[index] ? "bg-blue-50 border-blue-300" : "bg-gray-50 border-gray-300"
                        } ${errors.otp ? "border-red-300" : ""}`}
                    />
                  )}
                />
              </div>

              <input type="hidden" {...register("otp")} />

              {errors.otp && (
                <p className="text-red-600 text-center">{errors.otp.message}</p>
              )}

              <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg">
                <span className="text-gray-600">لم تستقبل الرمز؟</span>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={timer > 0 || isSending}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${timer > 0 || isSending
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-blue-600 hover:bg-blue-50"
                    }`}
                >
                  {timer > 0 ? `انتظر ${timer}s` : isSending ? "جاري الإرسال..." : "إعادة الإرسال"}
                </button>
              </div>

              <Button
                type="submit"
                isLoading={isLoading}
                disabled={isLoading || otp.length < 6}
                className="w-full h-12 text-lg"
              >
                {isLoading ? "جاري التحقق..." : "تأكيد الرمز"}
              </Button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

OTPForm.propTypes = {
  phoneNumber: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
};